import { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { Order } from '@/supabase/order/types/order';
import { OrderStatus } from '@/components/OrderControls';
import { CustomerTemplate } from '@/components/CustomerTemplateManager';
import { CombinedShippingModal } from '@/components/CombinedShippingModal';
import * as orderService from '@/supabase/order/service/orderService';
import { toast } from 'sonner';

export interface CombinedShippingSource {
  /** 현재 날짜 고객 id들의 CSV (batch는 다건, detail은 단건). split(',') 인코딩 계약 유지. */
  customerId: string;
  status: OrderStatus;
}

export interface CombinedShippingModalHandle {
  /**
   * 합배송 모달을 연다. Home 상태를 건드리지 않으므로 Home 본체 재렌더를 유발하지 않는다.
   * 모달은 관련 주문 fetch가 끝난 뒤에만 열리므로 로딩 상태가 필요 없다.
   */
  open: (orders: Order[], source: CombinedShippingSource) => void;
}

interface CombinedShippingModalContainerProps {
  /** 단일 고객 모드 모달 타이틀. 기존 JSX의 live read(`activeCustomer?.nickname`)를 그대로 prop으로 전달. */
  nickname: string;
  /**
   * confirm이 *커밋된 최신* 값으로 읽어야 하는 Home 값들. 매 commit마다 latestRef에 동기화되어
   * stable한 confirm 핸들러가 stale closure 없이 항상 최신을 본다(#38 latest-ref 패턴).
   * customerTemplates는 unmemoized live — snapshot 금지(stale면 현재날짜 분류가 틀려 주문 누락).
   */
  customerTemplates: CustomerTemplate[];
  proceedWithStatusUpdate: (customerId: string, status: OrderStatus) => Promise<boolean>;
  refreshOrders: () => void;
  setSelectedCustomerIds: (ids: string[]) => void;
  // 과거 주문 직접 status 쓰기 barrier(스펙 §16.10) — 관련 주문(±30일)에는 현재 날짜의 다른 주문도
  // 포함될 수 있어(id 제외만) 자동전환 reconcile이 이 쓰기를 되돌릴 수 있다. 현재날짜 경로는
  // proceedWithStatusUpdate 내부 barrier가 커버 — 여기는 직접 updateOrderStatus 경로만.
  onStatusWriteStart?: (orderId: number) => void;
  onStatusWriteEnd?: (orderId: number) => void;
}

/**
 * 합배송 모달을 Home에서 분리해 자체 관리한다.
 * - 열림 상태/관련 주문/소스 정보를 내부 state로 보유.
 * - `open(orders, source)`를 imperative handle로 노출 → Home은 ref 호출만 하므로 모달 오픈 시 Home이 재렌더되지 않는다.
 * - confirm은 컨테이너 local state(orders/source) + Home live 값(latestRef)으로 처리.
 *
 * ⚠️ React.memo로 감싸지 말 것. latestRef 동기화(아래 useLayoutEffect)는 Home commit마다 이 컴포넌트가
 *    재렌더되는 데 의존한다. memo로 prop-무변화 시 재렌더가 생략되면 layoutEffect가 안 돌아 latestRef가
 *    stale → '모달 열어둔 채 날짜/고객 변경 후 confirm'이 옛 customerTemplates로 현재날짜 주문을 잘못
 *    분류해 누락시킬 수 있다. (현재는 4개 prop이 매 렌더 새 참조라 memo여도 재렌더되지만, 의존 금지.)
 */
export const CombinedShippingModalContainer = forwardRef<
  CombinedShippingModalHandle,
  CombinedShippingModalContainerProps
>(({ nickname, customerTemplates, proceedWithStatusUpdate, refreshOrders, setSelectedCustomerIds, onStatusWriteStart, onStatusWriteEnd }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [source, setSource] = useState<CombinedShippingSource | null>(null);

  // #38 latest-ref: confirm을 안정 핸들러로 두되 항상 *커밋된* 최신 Home 값을 읽게 한다.
  // deps 없는 useLayoutEffect로 commit 직후(브라우저 paint·event 처리 전) 동기화 →
  // render body write가 아니므로 concurrent rendering에서 버려진 렌더 값이 새지 않는다.
  const latestRef = useRef({ customerTemplates, proceedWithStatusUpdate, refreshOrders, setSelectedCustomerIds, onStatusWriteStart, onStatusWriteEnd });
  useLayoutEffect(() => {
    latestRef.current = { customerTemplates, proceedWithStatusUpdate, refreshOrders, setSelectedCustomerIds, onStatusWriteStart, onStatusWriteEnd };
  });

  const open = useCallback<CombinedShippingModalHandle['open']>((nextOrders, nextSource) => {
    setOrders(nextOrders);
    setSource(nextSource);
    setIsOpen(true);
  }, []);

  useImperativeHandle(ref, () => ({ open }), [open]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setOrders([]);
    setSource(null);
  }, []);

  const handleConfirm = useCallback(async (newlySelectedOrderIds: number[]) => {
    if (!source) return;
    const { customerTemplates, proceedWithStatusUpdate, refreshOrders, setSelectedCustomerIds, onStatusWriteStart, onStatusWriteEnd } = latestRef.current;

    const sourceCustomerIds = source.customerId.split(',');

    // 1. 현재 날짜에 있는 주문들 (로컬 상태 업데이트 필요)
    const currentDayCustomerIds = sourceCustomerIds.filter(id => customerTemplates.some(c => c.id === id));

    // 2. 모달에서 선택된 과거 주문들 (DB 직접 업데이트 필요)
    const pastOrdersToUpdate = orders.filter(order => newlySelectedOrderIds.includes(order.id));

    const promises: Promise<unknown>[] = [];
    let keptOrdersCount = 0;

    // 현재 날짜 주문 처리
    currentDayCustomerIds.forEach(id => {
      const customer = customerTemplates.find(c => c.id === id);
      if (customer?.is_keep) keptOrdersCount++;
      promises.push(proceedWithStatusUpdate(id, source.status));
    });

    // 과거 날짜 주문 처리 — 직접 status 쓰기 barrier(§16.10): 관련 주문에 현재 날짜의 다른 주문이
    // 포함될 수 있어(±30일, id 제외만) 자동전환 reconcile의 되돌림 창을 닫는다.
    pastOrdersToUpdate.forEach(order => onStatusWriteStart?.(order.id));
    pastOrdersToUpdate.forEach(order => {
      if (order.is_keep) keptOrdersCount++;
      promises.push(orderService.updateOrderStatus(order.id, source.status));
      if (order.is_keep) {
        promises.push(orderService.updateOrderKeepStatus(order.id, false));
      }
    });

    await Promise.allSettled(promises);
    pastOrdersToUpdate.forEach(order => onStatusWriteEnd?.(order.id));

    const totalUpdated = currentDayCustomerIds.length + pastOrdersToUpdate.length;
    let successMessage = `${totalUpdated}개의 주문이 '출고 완료' 처리되었습니다.`;
    if (keptOrdersCount > 0) {
      successMessage += ` (${keptOrdersCount}개 '킵' 해제)`;
    }
    toast.success(successMessage);

    // 모달 닫고 상태 초기화
    setIsOpen(false);
    setOrders([]);
    setSource(null);
    setSelectedCustomerIds([]);

    // 현재 날짜의 주문 목록을 최신 상태로 갱신
    refreshOrders();
  }, [orders, source]);

  return (
    <CombinedShippingModal
      isOpen={isOpen}
      isLoading={false}
      orders={orders}
      nickname={nickname}
      sourceOrderCount={source?.customerId.split(',').length}
      onClose={handleClose}
      onConfirm={handleConfirm}
    />
  );
});

CombinedShippingModalContainer.displayName = 'CombinedShippingModalContainer';
